/**
 * Course AUTHORING editor: meta (title/description/category), the
 * course→unit→MODULE structure (F2-A spine: lesson|resource|… via
 * CourseModuleRegistry) and PUBLISHING to the catalog. Creating a lesson
 * ALWAYS creates its `course_module` (kind='lesson', ref_id=lesson.id). The
 * "add module" menu is driven by the registry × course_settings.
 * enabled_module_kinds. Writes to PowerSync (local-first); the server
 * applies author/editor authz and seals the version.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { msg } from "@lingui/core/macro";
import { Plus, Sparkles, Trash2 } from "@fleet/icons";
import {
  addAssignmentModuleMutation,
  addBlockMutation,
  addCourseModuleMutation,
  addForumModuleMutation,
  addLessonModuleMutation,
  addResourceModuleMutation,
  addUnitMutation,
  AI_AUTHORING_KEY,
  deleteAssignmentMutation,
  deleteBlockMutation,
  deleteCourseModuleMutation,
  deleteLessonMutation,
  deleteResourceMutation,
  deleteUnitMutation,
  parseBlockContent,
  publishCourseMutation,
  restoreCourseModuleCascadeMutation,
  unpublishCourseMutation,
  updateBlockMutation,
  updateCourseMutation,
  updateLessonMutation,
  updateUnitMutation,
} from "@space/shared";
import type { CourseModule, Lesson } from "@space/powersync";
import { useUndo } from "@/lib/undo";
import { stableRows } from "@/lib/solid/stable-rows";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useCourseTree, useCourseWrite, useLessonBlocks } from "../../hooks/use-course-data";
import { useCourseModules, useCourseSettings } from "@/features/academy/hooks/use-course-modules";
import { useActiveGradingScale } from "@/features/academy/hooks/use-grading-scale";
import { MODULE_KIND_META, moduleRegistry } from "@/features/academy/modules/registry";
// FLAGS: kinds gated by feature (active org + course), registry-driven.
import { useModuleKindFeatures } from "@/features/flags/use-module-kind-features";
import { ResourceModuleEditor } from "@/features/academy/modules/resource-module";
// MEDIA-F3F: authoring editor for the `video` module
import { VideoModuleEditor } from "@/features/media/components/video-module";
import { AssignmentModuleEditor } from "@/features/academy/modules/assignment-module";
// F3-C: authoring editor for the `forum` module
import { ForumModuleEditor } from "@/features/academy/modules/forum-module";
import { BLOCK_KINDS, defaultDefFor } from "../../lib/registry";
import { BlockEditor } from "./block-editor";
// B8 · AI authoring: proposes, never writes. Piece-by-piece review is where
// the TWO validation registries are cross-checked before applying.
import { AiGenerateDialog } from "@/features/ai-authoring/ai-generate-dialog";
import { AiProposalReview } from "@/features/ai-authoring/ai-proposal-review";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 focus-visible:border-primary focus-visible:outline-none";

/** Per-unit spine entry: a real module or a legacy lesson with no module. */
interface SpineEntry {
  key: string;
  kind: string;
  position: number;
  module: CourseModule | null;
  lesson: Lesson | null;
}

export const CourseEditor: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const courseId = () => props.courseId;
  const { drizzle } = useCourseWrite();
  const { course, units, lessons } = useCourseTree(courseId);
  const modules = useCourseModules(courseId);
  const settings = useCourseSettings(courseId);
  const [selectedLesson, setSelectedLesson] = createSignal<string | null>(null);
  // B8 · AI authoring. The open proposal is stored here so the review
  // survives the write-path's echoes (the editor doesn't remount).
  const aiAuthoring = useFeatureEnabled(AI_AUTHORING_KEY, { courseId });
  const [aiOpen, setAiOpen] = createSignal(false);
  const [aiProposalId, setAiProposalId] = createSignal<string | undefined>();

  // STABLE identity by id (F4-D): every write-path echo (which also re-seals
  // course.version/updated_at) re-emits new objects; without this the tree's
  // `<For>`s REMOUNT the whole subtree (killing interactive state: open
  // dialogs, focus, uploads in progress).
  // `updated_at` is re-sealed by the server; `created_at` only changes in
  // FORMAT (local ISO vs. the echo's timestamptz::text).
  const VOLATILE = ["updated_at", "created_at"];
  const stableUnits = stableRows(() => units.data ?? undefined, { ignore: VOLATILE });
  const stableLessons = stableRows(() => lessons.data ?? undefined, { ignore: VOLATILE });
  const stableModules = stableRows(() => modules.data ?? undefined, {
    ignore: VOLATILE,
    jsonFields: ["config"],
  });

  // Block-editing drafts, HOISTED to the editor's root (which does NOT
  // remount) — survive sync's re-emissions (see CC history).
  const [blockDrafts, setBlockDrafts] = createStore<Record<string, Record<string, unknown>>>({});

  const db = () => drizzle();
  const isPublished = () => course.data?.visibility === "public";

  /**
   * Fires a verified write mutation (`patchRowVerified`, see
   * `courses/mutations.ts`) without waiting for it to land, but WITHOUT
   * dropping the rejection: now that `unit`/`lesson`/`block` RE-READ and can
   * throw, a bare `void mutation(...)` would leave the failure as an
   * unhandled rejection and the field would keep showing a value that never
   * got saved.
   */
  const fireVerified = (promise: Promise<unknown>, errorMessage: string): void => {
    void promise.catch(() => {
      showToast(errorMessage, "error");
    });
  };

  const lessonById = createMemo(() => {
    const map = new Map<string, Lesson>();
    for (const l of stableLessons()) {
      map.set(l.id, l);
    }
    return map;
  });

  /**
   * Per-unit spine: `course_module`s are in charge (ordered by position).
   * Lessons with NO module (pre-migration data not yet backfilled locally)
   * are appended as legacy entries so as not to hide content.
   */
  // Spine entry cache: as long as module/lesson keep the SAME reference
  // (stableRows), the entry keeps its own and `<For>` doesn't remount.
  let spineEntryCache = new Map<string, SpineEntry>();
  const keepEntry = (entry: SpineEntry, nextCache: Map<string, SpineEntry>): SpineEntry => {
    const prev = spineEntryCache.get(entry.key);
    const kept =
      prev &&
      prev.kind === entry.kind &&
      prev.position === entry.position &&
      prev.module === entry.module &&
      prev.lesson === entry.lesson
        ? prev
        : entry;
    nextCache.set(entry.key, kept);
    return kept;
  };

  const spineByUnit = createMemo(() => {
    const map = new Map<string, SpineEntry[]>();
    const referenced = new Set<string>();
    const nextCache = new Map<string, SpineEntry>();
    for (const m of stableModules()) {
      const unitId = m.unit_id ?? "";
      const arr = map.get(unitId) ?? [];
      const lesson = m.kind === "lesson" && m.ref_id ? (lessonById().get(m.ref_id) ?? null) : null;
      if (m.kind === "lesson" && m.ref_id) {
        referenced.add(m.ref_id);
      }
      // A lesson module whose detail hasn't synced yet isn't rendered
      // (it arrives in the same stream; avoids ghost rows).
      if (m.kind === "lesson" && !lesson) {
        continue;
      }
      arr.push(
        keepEntry(
          {
            key: m.id,
            kind: m.kind ?? "lesson",
            position: m.position ?? 0,
            module: m,
            lesson,
          },
          nextCache,
        ),
      );
      map.set(unitId, arr);
    }
    for (const l of stableLessons()) {
      if (referenced.has(l.id)) {
        continue;
      }
      const unitId = l.unit_id ?? "";
      const arr = map.get(unitId) ?? [];
      arr.push(
        keepEntry(
          {
            key: `legacy-${l.id}`,
            kind: "lesson",
            position: l.position ?? 0,
            module: null,
            lesson: l,
          },
          nextCache,
        ),
      );
      map.set(unitId, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.position - b.position);
    }
    spineEntryCache = nextCache;
    return map;
  });

  /**
   * Enabled kinds: registry × course_settings.enabled_module_kinds × FLAGS
   * (the kind's feature effectively-enabled for the active org + course) —
   * a kind disabled by the academy disappears from the "add module" menu.
   */
  const kindFeature = useModuleKindFeatures(() => props.courseId);
  // Course's active scale: this is where a new assignment's max comes from.
  const gradingScale = useActiveGradingScale({ courseId: () => props.courseId });
  const enabledKindMeta = createMemo(() => {
    const enabled = new Set(moduleRegistry.enabledKinds(settings.data?.enabled_module_kinds));
    return MODULE_KIND_META.filter((m) => enabled.has(m.kind) && kindFeature()(m.kind).enabled);
  });

  /**
   * Autosave for the course's meta (title/description/category), triggered
   * on LEAVING the field (`change`).
   *
   * Used to be `void updateCourseMutation(...)`: fire-and-forget against
   * PowerSync's SQLite, with the promise DROPPED. Two consequences, and
   * both have been observed: if the write failed, the rejection got lost in
   * the `void` and the user was left staring at their text in a field that
   * no longer represented anything saved; and if the write was still in
   * flight when navigating away, it died with the page. The title reverted
   * to "Nuevo curso" with no one saying a word — it's the same bug as
   * `saveBlock` (fix d791223), which was left unclosed here.
   *
   * Now it's AWAITED, the failure is communicated, and `aria-busy` publishes
   * the saving state: a screen reader learns the field is saving and a test
   * can wait for it to land instead of guessing.
   *
   * CC-75: there was an `if (!d) return` that DROPPED the edit without
   * saying anything if the write layer wasn't resolved yet (PowerSync's
   * SQLite and the session resolve asynchronously and can be re-created).
   * The text stayed on screen and the database kept "Nuevo curso" forever.
   * Now it WAITS for it to appear and, if it doesn't, communicates it —
   * never discards silently.
   */
  const [savingMeta, setSavingMeta] = createSignal(false);

  /** Waits for the local write layer to be ready (≈5s) or returns null. */
  const writeLayer = async (): Promise<ReturnType<typeof db>> => {
    for (let waited = 0; waited < 5000; waited += 100) {
      const d = db();
      if (d) {
        return d;
      }
      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
    }
    return db();
  };

  const saveMeta = async (patch: Record<string, unknown>) => {
    setSavingMeta(true);
    try {
      const d = await writeLayer();
      if (!d) {
        throw new Error("PowerSync is not ready");
      }
      await updateCourseMutation(d, props.courseId, patch);
    } catch {
      showToast(t(msg`Could not save the course details`), "error");
    } finally {
      setSavingMeta(false);
    }
  };

  /**
   * Publish/unpublish. Both mutations RE-READ the row to confirm the change
   * landed (see `patchRowVerified`), so they can now throw: without this
   * `catch` the rejection would come out as an unhandled rejection and the
   * user would keep seeing the button as if it had published.
   */
  const togglePublish = async () => {
    const d = await writeLayer();
    if (!d) {
      return;
    }
    const wasPublished = isPublished();
    try {
      if (wasPublished) {
        await unpublishCourseMutation(d, props.courseId);
      } else {
        await publishCourseMutation(d, props.courseId);
      }
    } catch {
      showToast(t(msg`Could not save the course details`), "error");
      return;
    }
    showToast(
      wasPublished ? t(msg`Removed from the catalog`) : t(msg`Published to the public catalog`),
      "success",
    );
  };

  const addUnit = async () => {
    const d = await writeLayer();
    if (d) {
      void addUnitMutation(d, props.courseId, { position: units.data?.length ?? 0 });
    }
  };

  const addModule = async (unitId: string, kind: string) => {
    const d = await writeLayer();
    if (!d) {
      return;
    }
    const position = spineByUnit().get(unitId)?.length ?? 0;
    if (kind === "lesson") {
      void addLessonModuleMutation(d, { courseId: props.courseId, unitId, position });
    } else if (kind === "resource") {
      void addResourceModuleMutation(d, { courseId: props.courseId, unitId, position });
    } else if (kind === "video") {
      // MEDIA-F3F: `video` module — no detail entity; the video is picked in
      // the editor and stored in `config.videoAssetId`.
      void addCourseModuleMutation(d, {
        courseId: props.courseId,
        unitId,
        kind: "video",
        position,
        config: {},
      });
    } else if (kind === "assignment") {
      void addAssignmentModuleMutation(d, {
        courseId: props.courseId,
        unitId,
        position,
        scale: gradingScale(),
      });
    } else if (kind === "forum") {
      // F3-C: `forum` module — creates the `forum` row (deterministic id) + module.
      // The module→forum link uses `patchRowVerified` and can throw.
      void addForumModuleMutation(d, { courseId: props.courseId, unitId, position }).catch(
        (err: unknown) => {
          console.error("Error adding forum module:", err);
          showToast(t(msg`Could not add the forum`), "error");
        },
      );
    } else {
      // LF-B: kinds with no detail entity of their own (live_session and
      // future registry ones). The menu button used to exist but this
      // handler had no branch → click with NO EFFECT (a live class couldn't be added).
      void addCourseModuleMutation(d, {
        courseId: props.courseId,
        unitId,
        kind,
        position,
        config: {},
      });
    }
  };

  const { registerUndo } = useUndo();
  const removeEntry = (entry: SpineEntry) => {
    const d = db();
    if (!d) {
      return;
    }
    const moduleId = entry.module?.id;
    // F4-A: every delete is soft; undo restores module + ref as a pair.
    // `deleteLessonMutation` now RE-READS and can throw (see
    // `verified-write.ts`): without this catch, the rejection would come out
    // as an unhandled rejection and the module would keep pretending to be
    // deleted in the UI even though the local row remained intact.
    void (async () => {
      try {
        if (entry.module) {
          await deleteCourseModuleMutation(d, entry.module.id);
        }
        if (entry.kind === "lesson" && entry.lesson) {
          await deleteLessonMutation(d, entry.lesson.id);
        }
        if (entry.kind === "resource" && entry.module?.ref_id) {
          await deleteResourceMutation(d, entry.module.ref_id);
        }
        if (entry.kind === "assignment" && entry.module?.ref_id) {
          await deleteAssignmentMutation(d, entry.module.ref_id);
        }
      } catch {
        showToast(t(msg`Could not delete this module`), "error");
        return;
      }
      if (moduleId) {
        registerUndo(t(msg`Module moved to trash`), async () => {
          const dd = db();
          if (dd) {
            await restoreCourseModuleCascadeMutation(dd, moduleId);
          }
        });
      }
    })();
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <div class="flex items-center justify-between gap-2">
        <Link to="/authoring" class="text-sm text-muted-foreground hover:text-foreground">
          ← {t(msg`My courses`)}
        </Link>
        {/* FLAGS: AI entry point only if the academy turned the capability on.
            The server remains authoritative (structured 403); this avoids
            the 403, it doesn't replace it. */}
        <Show when={aiAuthoring().enabled}>
          <Button
            variant="outline"
            size="sm"
            data-testid="ai-authoring-open"
            onClick={() => {
              setAiOpen(true);
            }}
          >
            <Sparkles class="mr-1 h-4 w-4" />
            {t(msg`AI`)}
          </Button>
        </Show>
      </div>

      <Show when={aiAuthoring().enabled}>
        <AiGenerateDialog
          open={aiOpen()}
          onOpenChange={setAiOpen}
          courseId={props.courseId}
          onCreated={setAiProposalId}
        />
        <Show when={aiProposalId()}>
          {(id) => (
            <AiProposalReview
              proposalId={id()}
              courseId={props.courseId}
              onClose={() => {
                setAiProposalId(undefined);
              }}
            />
          )}
        </Show>
      </Show>

      <Show when={!course.isLoading} fallback={<Skeleton class="h-40 w-full rounded-2xl" />}>
        {/* Meta */}
        <div
          class="space-y-3 rounded-2xl border-2 border-border p-5"
          data-testid="course-meta"
          aria-busy={savingMeta() ? "true" : "false"}
        >
          <input
            class={`${field} text-xl font-bold`}
            data-testid="edit-course-title"
            value={course.data?.title ?? ""}
            onChange={(e) => {
              void saveMeta({ title: e.currentTarget.value });
            }}
            placeholder={t(msg`Course title`)}
          />
          <textarea
            class={field}
            data-testid="edit-course-description"
            rows={2}
            value={course.data?.description ?? ""}
            onChange={(e) => {
              void saveMeta({ description: e.currentTarget.value });
            }}
            placeholder={t(msg`Description`)}
          />
          <input
            class={field}
            data-testid="edit-course-category"
            value={course.data?.category ?? ""}
            onChange={(e) => {
              void saveMeta({ category: e.currentTarget.value });
            }}
            placeholder={t(msg`Category (e.g. Languages)`)}
          />
          <div class="flex items-center justify-between pt-1">
            <span class="text-sm text-muted-foreground">
              {t(msg`Status`)}:{" "}
              <span
                data-testid="course-visibility"
                data-published={isPublished() ? "true" : "false"}
              >
                {isPublished() ? t(msg`Public`) : t(msg`Draft`)}
              </span>{" "}
              · {t(msg`version {version}`, { version: course.data?.version ?? 1 })}
            </span>
            <Button
              data-testid="course-publish-toggle"
              variant={isPublished() ? "outline" : "default"}
              onClick={togglePublish}
            >
              {isPublished() ? t(msg`Unpublish`) : t(msg`Publish`)}
            </Button>
          </div>
        </div>

        {/* Structure: unit → modules (spine) */}
        <div class="space-y-4" data-testid="editor-units">
          <For each={stableUnits()}>
            {(u) => (
              <div class="rounded-2xl border border-border p-4">
                <div class="mb-2 flex items-center gap-2">
                  <input
                    class={`${field} font-semibold`}
                    data-testid="edit-unit-title"
                    value={u.title ?? ""}
                    onChange={(e) => {
                      const d = db();
                      if (d) {
                        fireVerified(
                          updateUnitMutation(d, u.id, { title: e.currentTarget.value }),
                          t(msg`Could not save the unit`),
                        );
                      }
                    }}
                  />
                  <button
                    type="button"
                    data-testid="delete-unit"
                    onClick={() => {
                      const d = db();
                      if (d) {
                        fireVerified(
                          deleteUnitMutation(d, u.id),
                          t(msg`Could not delete the unit`),
                        );
                      }
                    }}
                    class="text-muted-foreground hover:text-danger"
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
                <ul class="space-y-1" data-testid="unit-modules">
                  <For each={spineByUnit().get(u.id) ?? []}>
                    {(entry) => (
                      <Show
                        when={entry.kind === "lesson" && entry.lesson}
                        fallback={
                          <li data-testid="editor-module" data-module-kind={entry.kind}>
                            <div class="mt-1 flex items-start gap-2">
                              <span class="mt-2 rounded-full bg-joy-berry/15 px-2 py-0.5 text-[11px] font-bold text-joy-berry">
                                {entry.kind}
                              </span>
                              <div class="flex-1">
                                <Show when={entry.kind === "resource" && entry.module}>
                                  <ResourceModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* MEDIA-F3F: authoring for the `video` module */}
                                <Show when={entry.kind === "video" && entry.module}>
                                  <VideoModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* end MEDIA-F3F */}
                                {/* F3-B: authoring for the `assignment` module */}
                                <Show when={entry.kind === "assignment" && entry.module}>
                                  <AssignmentModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* F3-C: authoring for the `forum` module */}
                                <Show when={entry.kind === "forum" && entry.module}>
                                  <ForumModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* end F3-C */}
                              </div>
                              <button
                                type="button"
                                data-testid="delete-module"
                                onClick={() => removeEntry(entry)}
                                class="mt-2 text-muted-foreground hover:text-danger"
                              >
                                <Trash2 class="h-4 w-4" />
                              </button>
                            </div>
                          </li>
                        }
                      >
                        {(_) => {
                          const l = entry.lesson!;
                          return (
                            <li data-testid="editor-module" data-module-kind="lesson">
                              <div class="flex items-center gap-2">
                                <button
                                  type="button"
                                  data-testid="select-lesson"
                                  onClick={() =>
                                    setSelectedLesson(selectedLesson() === l.id ? null : l.id)
                                  }
                                  class={`flex-1 rounded-lg border px-3 py-2 text-left text-sm ${selectedLesson() === l.id ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted/50"}`}
                                >
                                  {l.title}
                                </button>
                                <input
                                  type="number"
                                  class="w-16 rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm"
                                  data-testid="edit-lesson-xp"
                                  value={l.xp_reward ?? 10}
                                  onChange={(e) => {
                                    const d = db();
                                    if (d) {
                                      fireVerified(
                                        updateLessonMutation(d, l.id, {
                                          xp_reward: Number(e.currentTarget.value),
                                        }),
                                        t(msg`Could not save the lesson`),
                                      );
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  data-testid="delete-lesson"
                                  onClick={() => removeEntry(entry)}
                                  class="text-muted-foreground hover:text-danger"
                                >
                                  <Trash2 class="h-4 w-4" />
                                </button>
                              </div>
                              <Show when={selectedLesson() === l.id}>
                                <div class="mt-2">
                                  <input
                                    class={`${field} mb-2 text-sm`}
                                    data-testid="edit-lesson-title"
                                    value={l.title ?? ""}
                                    onChange={(e) => {
                                      const d = db();
                                      if (d) {
                                        fireVerified(
                                          updateLessonMutation(d, l.id, {
                                            title: e.currentTarget.value,
                                          }),
                                          t(msg`Could not save the lesson`),
                                        );
                                      }
                                    }}
                                  />
                                  <LessonBlocks
                                    courseId={props.courseId}
                                    lessonId={l.id}
                                    drafts={blockDrafts}
                                    setDraft={(id, def) => setBlockDrafts(id, def)}
                                  />
                                </div>
                              </Show>
                            </li>
                          );
                        }}
                      </Show>
                    )}
                  </For>
                </ul>
                {/* "Add module" menu: registry × enabled_module_kinds. */}
                <div class="mt-2 flex flex-wrap gap-2" data-testid="add-module-menu">
                  <For each={enabledKindMeta()}>
                    {(meta) => (
                      <button
                        type="button"
                        data-testid={meta.testId}
                        onClick={() => addModule(u.id, meta.kind)}
                        class="flex items-center gap-1 text-sm text-primary hover:underline"
                        title={t(meta.hint)}
                      >
                        <Plus class="h-3 w-3" /> {t(meta.label)}
                      </button>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
          <Button variant="outline" data-testid="add-unit" onClick={addUnit} class="gap-2">
            <Plus class="h-4 w-4" /> {t(msg`Add unit`)}
          </Button>
        </div>
      </Show>
    </div>
  );
};

/** A lesson's blocks: create (pick a type), edit `def`, save, delete. */
const LessonBlocks: Component<{
  courseId: string;
  lessonId: string;
  /** Drafts hoisted to the root (survive sync's re-emissions). */
  drafts: Record<string, Record<string, unknown>>;
  setDraft: (id: string, def: Record<string, unknown>) => void;
}> = (props) => {
  const { t } = useI18n();
  const { drizzle } = useCourseWrite();
  const blocks = useLessonBlocks(() => props.lessonId);
  // Stable identity by id: every sync echo re-emits NEW objects and `<For>`
  // (keyed by reference) used to REMOUNT every BlockEditor — killing internal
  // interactive state (e.g. the "insert video" dialog would close on its
  // own, and an in-progress upload would die too). The drafts were already
  // hoisted to the root for this same reason; this protects the rest of the state.
  // `updated_at` is re-sealed by the server; `created_at` only changes
  // format; `content` is jsonb re-serialized by the echo (structural equality).
  const stableBlocks = stableRows(() => blocks.data ?? undefined, {
    ignore: ["updated_at", "created_at"],
    jsonFields: ["content"],
  });

  const db = () => drizzle();
  // PURE read (no effects): the live draft or, if not edited yet, the
  // server's parsed `def`.
  const draftFor = (id: string, content: unknown): Record<string, unknown> =>
    props.drafts[id] ?? parseBlockContent(content);

  const addBlock = (kind: string) => {
    const d = db();
    if (d) {
      void addBlockMutation(d, {
        courseId: props.courseId,
        lessonId: props.lessonId,
        kind,
        content: defaultDefFor(kind),
        position: blocks.data?.length ?? 0,
      });
    }
  };
  /**
   * Saving a block WAITS for the local write to land before declaring
   * victory. It used to be `void updateBlockMutation(...)` + an immediate
   * toast: fire-and-forget against PowerSync's SQLite. If the user navigated
   * (or the test did `page.goto`) in the window between the click and the
   * commit, the write DIED with the page — the block was left with the
   * default `def` (empty prompt ⇒ invalid definition ⇒ the engine scores it
   * 0) and had still been told "Block saved". Now the toast is the real
   * confirmation and a failure is communicated instead of swallowed.
   */
  const saveBlock = async (id: string, content: unknown) => {
    const d = db();
    if (!d) {
      return;
    }
    try {
      await updateBlockMutation(d, id, { content: draftFor(id, content) });
      showToast(t(msg`Block saved`), "success");
    } catch {
      showToast(t(msg`The block could not be saved`), "error");
    }
  };

  return (
    <div class="space-y-3 rounded-lg bg-muted/30 p-3" data-testid="lesson-blocks">
      <For each={stableBlocks()}>
        {(b) => (
          <div class="rounded-lg border border-border bg-background p-3" data-testid="editor-block">
            <div class="mb-2 flex items-center justify-between">
              <span class="rounded-full bg-accent px-2 py-0.5 text-xs text-accent-foreground">
                {b.kind}
              </span>
              <div class="flex gap-2">
                <button
                  type="button"
                  data-testid="save-block"
                  onClick={() => void saveBlock(b.id, b.content)}
                  class="text-xs text-primary hover:underline"
                >
                  {t(msg`Save`)}
                </button>
                <button
                  type="button"
                  data-testid="delete-block"
                  onClick={() => {
                    const d = db();
                    if (d) {
                      void deleteBlockMutation(d, b.id).catch(() => {
                        showToast(t(msg`The block could not be deleted`), "error");
                      });
                    }
                  }}
                  class="text-muted-foreground hover:text-danger"
                >
                  <Trash2 class="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <BlockEditor
              kind={b.kind ?? "quiz"}
              def={draftFor(b.id, b.content)}
              onChange={(def) => props.setDraft(b.id, def)}
              courseId={props.courseId}
            />
          </div>
        )}
      </For>
      <div class="flex flex-wrap gap-1.5">
        <For each={BLOCK_KINDS}>
          {(k) => (
            <button
              type="button"
              data-testid={`add-block-${k.kind}`}
              onClick={() => addBlock(k.kind)}
              class="rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary/50"
              title={t(k.hint)}
            >
              + {t(k.label)}
            </button>
          )}
        </For>
      </div>
    </div>
  );
};
